import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, ImagePlaceholder } from "@/components/site/SiteShell";
import { PageHero, Section, Breadcrumbs, Prose, FAQGrid } from "@/components/site/Sections";
import { ContactForm } from "@/components/site/ContactForm";

const TITLE = "Kontakt | Elektriker-Elektriker København";
const DESCRIPTION =
  "Kontakt Elektriker-Elektriker på +45 28 7000 28 eller el@elektriker-elektriker.dk. Døgnvagt 24/7 i hele Storkøbenhavn.";

export const Route = createFileRoute("/kontakt")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "/kontakt" },
    ],
    links: [{ rel: "canonical", href: "https://elektriker-elektriker.dk/kontakt" }],
  }),
  component: KontaktPage,
});

function KontaktPage() {
  return (
    <SiteShell>
      <Breadcrumbs items={[{ label: "Forside", to: "/" }, { label: "Kontakt" }]} />
      <PageHero
        variant="split"
        accentColor="oklch(0.72 0.16 130)"
        eyebrow="Kontakt · Døgnvagt 24/7"
        title="Ring eller skriv til os"
        intro="Vi er sjældent mere end et opkald væk. Vi har døgnvagt på telefonen 24/7 til akutte opgaver i hele Storkøbenhavn."
        imageLabel="Vores base på Østerbro"
        hideCta
      />

      <Section className="!pt-0">
        <div className="grid gap-4 md:grid-cols-2">
          <a href="tel:+4528700028" className="group rounded-2xl border border-border bg-card p-6 transition hover:border-primary">
            <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Ring direkte · Døgnvagt 24/7</div>
            <div className="mt-2 text-4xl font-bold text-primary md:text-5xl">+45 28 7000 28</div>
            <p className="mt-3 text-sm text-muted-foreground">Hverdage 7–17. Akut udrykning også uden for åbningstid og i weekenden.</p>
          </a>
          <a href="mailto:el@elektriker-elektriker.dk" className="group rounded-2xl border border-border bg-card p-6 transition hover:border-primary">
            <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Skriv en mail</div>
            <div className="mt-2 break-all text-3xl font-bold text-[var(--ink)] md:text-4xl">el@elektriker-elektriker.dk</div>
            <p className="mt-3 text-sm text-muted-foreground">Vi svarer typisk samme arbejdsdag.</p>
          </a>
        </div>
      </Section>


      <Section>
        <div className="grid gap-10 md:grid-cols-[1fr_1.2fr]">
          <div className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Telefon, Døgnvagt</h3>
              <a href="tel:+4528700028" className="mt-2 block text-3xl font-bold text-primary">+45 28 7000 28</a>
              <p className="mt-2 text-sm text-muted-foreground">Hverdage 7–17. Akut udrykning også uden for åbningstid og i weekenden.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">E-mail</h3>
              <a href="mailto:el@elektriker-elektriker.dk" className="mt-2 block text-lg font-semibold text-[var(--ink)]">el@elektriker-elektriker.dk</a>
              <p className="mt-2 text-sm text-muted-foreground">Vi svarer typisk samme arbejdsdag.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Adresse</h3>
              <p className="mt-2 text-[var(--ink)]/85">Østersøgade 106A<br />2100 København Ø</p>
              <p className="mt-3 text-sm text-muted-foreground">CVR: 35209468 · Autoriseret elinstallatør</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Åbningstider</h3>
              <dl className="mt-2 grid grid-cols-2 gap-y-1 text-sm text-[var(--ink)]/85">
                <dt>Mandag–fredag</dt><dd className="text-right">07:00–17:00</dd>
                <dt>Lørdag–søndag</dt><dd className="text-right">Døgnvagt akut</dd>
                <dt>Helligdage</dt><dd className="text-right">Døgnvagt akut</dd>
              </dl>
            </div>
          </div>
          <ContactForm variant="kontakt" />
        </div>
      </Section>

      <Section className="!pt-0">
        <Prose>
          <h2>Vi dækker hele Storkøbenhavn</h2>
          <p>
            Vores base og lager ligger på Østerbro, tæt på Trianglen og Svanemøllen. Herfra
            koordinerer vi vagtcentral, projekter og administration, og vi har kort vej til hele
            Storkøbenhavn. Typisk er vi fremme i Indre By eller Frederiksberg på under en time
            (også i myldretiden), og i Nordhavn, Nørrebro og resten af Østerbro indenfor under en
            halv time.
          </p>
          <p>
            Vi har biler ude i hele Storkøbenhavn hver eneste dag og kan derfor ofte rykke ud akut
            på Amager, i Hellerup, Charlottenlund, Søborg, Gladsaxe, Herlev, Valby og Vanløse
            indenfor 60-90 minutter.
          </p>
        </Prose>
      </Section>

      <Section className="!pt-0">
        <h2 className="text-3xl">Ofte stillede spørgsmål</h2>
        <div className="mt-8">
          <FAQGrid items={[
            { q: "Hvor hurtigt svarer I?", a: "På telefon: med det samme i åbningstiden. På mail: typisk samme arbejdsdag, senest næste morgen." },
            { q: "Kan jeg ringe efter kl. 17?", a: "Ja, vi har døgnvagt 24/7 til akutte opgaver. Akut udrykning udenfor åbningstid afregnes efter takst (se Priser)." },
            { q: "Hvor stort et område dækker I?", a: "Hele Storkøbenhavn og Nordsjælland. Vi servicerer også opgaver længere væk efter aftale." },
            { q: "Skal jeg booke besigtigelse?", a: "Til mindre opgaver giver vi tit pris på telefonen. Til større projekter aftaler vi en besigtigelse, typisk indenfor en uge." },
          ]} />
        </div>
      </Section>
    </SiteShell>
  );
}
